import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, ArrowRight, Check } from 'lucide-react';
import { ROAST } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Roast() {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setErr('Tell us your name.');
    if (!url.trim()) return setErr('Drop a URL we can roast.');
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/roast`, { name, website_url: url });
      if (data?.success) {
        setDone(true);
        setName(''); setUrl('');
      }
    } catch {
      setErr('Couldn’t reach us. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section pt-0" data-testid={ROAST.block}>
      <div className="container-pro">
        <div className="dark-panel p-7 md:p-12 relative overflow-hidden">
          {/* SVG hex decoration */}
          <svg className="absolute -top-12 -right-10 opacity-[0.06]" width="320" height="320" viewBox="0 0 320 320" aria-hidden>
            <g fill="none" stroke="#FFFFFF" strokeWidth="1">
              {Array.from({ length: 12 }).map((_, i) => {
                const r = 20 + i * 14;
                return (
                  <polygon
                    key={i}
                    points={`160,${160 - r} ${160 + r * 0.866},${160 - r / 2} ${160 + r * 0.866},${160 + r / 2} 160,${160 + r} ${160 - r * 0.866},${160 + r / 2} ${160 - r * 0.866},${160 - r / 2}`}
                  />
                );
              })}
            </g>
          </svg>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center relative">
            <div className="lg:col-span-2">
              <span className="eyebrow on-dark mb-4">FREE ROAST</span>
              <h3 className="font-display text-[1.7rem] md:text-[2rem] font-semibold leading-tight text-white mt-3">
                Got an existing site? <span className="italic text-[color:#F5C56D]">Send it over.</span>
              </h3>
              <p className="mt-3 text-white/65 text-[15px]">
                We’ll tell you exactly what’s killing your conversions — for free.
              </p>
            </div>
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    data-testid={ROAST.success}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3 p-4 rounded-xl border border-white/15 bg-white/5"
                  >
                    <span className="w-9 h-9 rounded-full inline-flex items-center justify-center bg-[color:var(--sh-amber)] text-white">
                      <Check size={15} />
                    </span>
                    <div>
                      <div className="font-display text-lg text-white">Locked and loaded.</div>
                      <div className="text-[13px] text-white/60">We’ll send the roast within 24 hours.</div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    data-testid={ROAST.form}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    noValidate
                  >
                    <input
                      data-testid={ROAST.name}
                      className="field"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#FFFFFF' }}
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <input
                      data-testid={ROAST.url}
                      className="field"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#FFFFFF' }}
                      placeholder="https://yourwebsite.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                    />
                    {err && <div className="md:col-span-2 text-sm text-red-300">{err}</div>}
                    <button
                      type="submit"
                      data-testid={ROAST.submit}
                      disabled={loading}
                      className="btn-amber md:col-span-2 inline-flex items-center justify-center gap-2"
                    >
                      <Flame size={15} /> {loading ? 'Sending…' : 'Roast My Site'} <ArrowRight size={15} />
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
